import { sInputKeyValidationHelper } from './shared.min.js';

class ChatInterface {
    constructor(config = {}) {
        const container = config.container;
        this.container = container;
        this.chatMessages = container.querySelector('#ac-chatMessages');
        this.scrollContainer = container.querySelector('.mod-ask-confused-ai__content');
        this.userInput = container.querySelector('#ac-userInput');
        this.sendButton = container.querySelector('#ac-sendButton');
        this.form = container.querySelector('.mod-ask-confused-ai__form');
        this.initialQuestionButtons = container.querySelectorAll('.initial-questions__button');
        this.choiceButtons = [];
        this.apiUrl = config.apiUrl || '';
        this.apiSubKey = config.apiSubKey || '';
        this.userJwt = config.userJwt || '';
        this.conversationId = sessionStorage.getItem('askConfusedConversationId') || null;
        sInputKeyValidationHelper(this.userInput, 'freeform');
        this.initEventListeners();
    }


    initEventListeners() {
        this.form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.sendMessage();
        });

        this.initialQuestionButtons.forEach((button) => {
            button.addEventListener('click', () => {
                const question = (button.dataset.question || button.textContent || '').trim();
                if (!question) return;

                this.userInput.value = '';
                this.userInput.value = question;
                this.userInput.dispatchEvent(new Event('input', { bubbles: true }));
                this.sendButton.click();
            });

            this.choiceButtons.push(button);
            
        });

        this.userInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.sendButton.click();
            }
        });

        this.userInput.addEventListener('input', () => {
            this.userInput.style.height = 'auto';
            this.userInput.style.height = Math.min(this.userInput.scrollHeight, 150) + 'px';
            this.sendButton.disabled = !this.userInput.value.trim();
        });
    }

    async sendMessage() {

        const message = this.userInput.value.trim();
        if (!message) return;

        this.addUserMessage(message);
        this.startLoading();

        if (window.dataLayer) {
            window.dataLayer.push({
                event: 'ask_confused_question_submitted',
                ...(this.conversationId !== null && { askConfusedConversationId: this.conversationId }),
                isFirstMessage: this.conversationId === null,
            });
        }

        try {
            const res = await fetch(this.apiUrl, {
                method: 'POST',
                // Lets the digital-analytics session cookies (HttpOnly, scoped to
                // .confused.com) ride along to api.confused.com so the gateway can
                // stamp them onto the conversation trace (ASK-296/ASK-297).
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Ocp-Apim-Subscription-Key': this.apiSubKey,
                    // Only signed-in journeys carry a token; logged-out sends none and is
                    // treated as unauthenticated by the gateway.
                    ...(this.userJwt && { 'Authorization': `Bearer ${this.userJwt}` }),
                },
                body: JSON.stringify(
                    {
                        message,
                        authenticated: this.getAuthenticatedUser(),
                        ...(this.conversationId !== null && { conversation_id: this.conversationId }),
                        location: {
                            platform: 'web'
                        }
                    }),
            });
            
            this.hideTypingIndicator();

            if (res.status === 404) {
                const data = await res.json();
                if (data.conversation_id) {
                    const isNew = this.conversationId === null;
                    this.setConversationId(data.conversation_id);
                    this.pushConversationResponseToDataLayer(data.conversation_id, isNew);
                }
                this.addAiMessage("Sorry, I couldn't find an answer to that. Please try rephrasing your question.");
                return;
            }

            if (!res.ok) {
                const err = new Error(`HTTP ${res.status} ${res.statusText}`);
                err.status = res.status;
                throw err;
            }

            const data = await res.json();

            const { message: responseMessage, conversation_id, links, follow_up_questions } = data;

            if (conversation_id) {
                const isNew = this.conversationId === null;
                this.setConversationId(conversation_id);
                this.pushConversationResponseToDataLayer(conversation_id, isNew);
            }

            this.addAiMessage(responseMessage, links, follow_up_questions);

        } catch (err) {
            this.hideTypingIndicator();
            if (err.status === 504) {
                this.addAiMessage("The request timed out. Please try again.");
            } else {
                this.addAiMessage("Something went wrong. Please try again later.");
            }
        } finally {
            this.setInputDisabled(false);
            // Skip refocus on touch devices — re-focusing the textarea forces the soft
            // keyboard open, which resizes the visual viewport mid-scroll and causes the
            // AI reply to jump out of view. Desktop users still get the convenience focus.
            if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
                this.userInput.focus();
            }
        }
    }

    // Authenticated means "we hold a signed token for this user", not "a cookie is present".
    // The previous cookie check only asserted that AuthenticatedUser= existed, which anyone
    // could set from devtools. A JWT is only minted server-side for a real signed-in identity
    // (see AskConfusedController), so token presence is the honest signal.
    getAuthenticatedUser() {
        return Boolean(this.userJwt);
    }

    setConversationId(id) {
        this.conversationId = id;
        sessionStorage.setItem('askConfusedConversationId', id);
    }

    pushConversationResponseToDataLayer(id, isNewConversation) {
        if (window.dataLayer) {
            window.dataLayer.push({
                event: isNewConversation ? 'ask_confused_conversation_started' : 'ask_confused_conversation_continued',
                askConfusedConversationId: id,
            });
        }
    }

    addUserMessage(message) {
        const messageDiv = document.createElement('div');
        messageDiv.className = 'message user-message';
        messageDiv.innerHTML = `
            <div class="message-content">
                ${this.escapeHtml(message)}
            </div>
        `;
        this.chatMessages.appendChild(messageDiv);
        // No scroll here — showTypingIndicator() fires immediately after and scrolls
        // once to the bottom, covering both the user message and the typing dots.
        // Two back-to-back smooth scrolls fight each other (and the keyboard) on mobile.
    }

    addAiMessage(message, links = [], choices = []) {
        const messageDiv = document.createElement('div');
        messageDiv.className = 'message ai-message';
        const sanitizedLinks = links
            .map(link => ({ ...link, safeUrl: this.sanitizeUrl(link.url) }))
            .filter(link => link.safeUrl);

        messageDiv.innerHTML = `
            <div class="message-content">
                ${this.formatMessage(message)}
            </div>
        `;

        if (sanitizedLinks.length > 0) {
            const linksWrap = document.createElement('div');
            linksWrap.className = 'message-links';
            const ul = document.createElement('ul');
            sanitizedLinks.forEach(link => {
                const li = document.createElement('li');
                const a = document.createElement('a');
                a.href = link.safeUrl;
                a.target = '_blank';
                a.rel = 'noopener noreferrer';
                a.textContent = this.decodeHtmlEntities(link.title);
                li.appendChild(a);
                ul.appendChild(li);
            });
            linksWrap.appendChild(ul);
            messageDiv.querySelector('.message-content').appendChild(linksWrap);
        }

        if (choices && choices.length > 0) {
            const choicesDiv = document.createElement('div');
            choicesDiv.className = 'choice-buttons';
            const choicesHeading = document.createElement('p');
            choicesHeading.className = 'choices-heading';
            choicesHeading.textContent = 'Other people asked:';
            choicesDiv.appendChild(choicesHeading);
            choices.forEach(choice => {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'choice-btn btn btn--inverse';
                // create a span here for the text to go in to
                const span = document.createElement('span');
                span.className = 'btn-text';
                span.textContent = this.decodeHtmlEntities(choice.text);
                btn.appendChild(span);
                btn.addEventListener('click', () => this.selectChoice(choice));
                choicesDiv.appendChild(btn);
                this.choiceButtons.push(btn);
            });
            messageDiv.appendChild(choicesDiv);
        }
        this.chatMessages.appendChild(messageDiv);
        this.scrollToMessageTop(messageDiv);
    }

    selectChoice(choice) {
        this.choiceButtons.forEach(btn => btn.disabled = true);
        this.userInput.value = this.decodeHtmlEntities(choice.text);
        this.sendMessage();
    }

    startLoading() {
        this.userInput.value = '';
        this.userInput.style.height = 'auto';
        this.setInputDisabled(true);
        this.showTypingIndicator();
    }

    showTypingIndicator() {
        this.sendButton.classList.add('loading');

        const typingDiv = document.createElement('div');
        typingDiv.className = 'message ai-message';
        typingDiv.innerHTML = `
            <div class="message-content typing-indicator" aria-hidden="true">
                <div class="typing-circle"></div>
                <div class="typing-circle"></div>
                <div class="typing-circle"></div>
            </div>
        `;
        this._typingIndicator = typingDiv;
        this.chatMessages.appendChild(typingDiv);
        this.scrollToBottom();
    }

    hideTypingIndicator() {
        if (this._typingIndicator) {
            this._typingIndicator.remove();
            this._typingIndicator = null;
        }
        this.sendButton.classList.remove('loading');
    }

    setInputDisabled(disabled) {
        this.choiceButtons.forEach(btn => btn.disabled = disabled);
        this.userInput.disabled = disabled;
        this.sendButton.disabled = disabled || !this.userInput.value.trim();
    }

    scrollToBottom() {
        if (!this.scrollContainer) return;
        this.scrollContainer.scrollTo({ top: this.scrollContainer.scrollHeight, behavior: 'smooth' });
    }

    scrollToMessageTop(el) {
        if (!this.scrollContainer) return;
        // scrollIntoView scrolls EVERY scrollable ancestor including the window, so it drags
        // the whole page instead of just the chat container. Scroll the container directly,
        // using a getBoundingClientRect delta so it works regardless of offsetParent, minus a 70px margin.
        const prefersInstant = !window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        // Defer one frame so the DOM insertion and any keyboard-driven viewport resize have
        // settled before we read the bounding rects.
        requestAnimationFrame(() => {
            // rect delta works no matter where el sits in the offsetParent chain.
            const delta = el.getBoundingClientRect().top - this.scrollContainer.getBoundingClientRect().top;
            this.scrollContainer.scrollTo({
                top: this.scrollContainer.scrollTop + delta - 70,
                behavior: prefersInstant ? 'auto' : 'smooth',
            });
        });
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    decodeHtmlEntities(text) {
        if (text == null) return '';
        const ta = document.createElement('textarea');
        ta.innerHTML = String(text);
        return ta.value;
    }

    sanitizeUrl(url) {
        try {
            const parsed = new URL(url, window.location.origin);
            if (!['http:', 'https:'].includes(parsed.protocol)) return '';
            return parsed.href;
        } catch {
            return '';
        }
    }

    formatMessage(text) {
        const codeBlocks = [];
        let processed = text.replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang, code) => {
            const idx = codeBlocks.length;
            codeBlocks.push(`<pre><code>${this.escapeHtml(code.trim())}</code></pre>`);
            return `__CB_${idx}__`;
        });

        const inlineCodes = [];
        processed = processed.replace(/`([^`]+)`/g, (_, code) => {
            const idx = inlineCodes.length;
            inlineCodes.push(`<code>${this.escapeHtml(code)}</code>`);
            return `__IC_${idx}__`;
        });

        const markdownLinks = [];
        processed = processed.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (_, text, url) => {
            const idx = markdownLinks.length;
            const safeUrl = this.sanitizeUrl(url);
            markdownLinks.push(safeUrl
                ? `<a href="${this.escapeHtml(safeUrl)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(text)}</a>`
                : this.escapeHtml(text));
            return `__ML_${idx}__`;
        });

        processed = this.escapeHtml(processed);

        processed = processed.replace(/^#{1,3} (.+)$/gm, '<h4>$1</h4>');

        processed = processed.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
        processed = processed.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        processed = processed.replace(/(^|[^*])\*(?!\*)(.+?)\*(?!\*)/g, '$1<em>$2</em>');

        processed = processed.replace(/(^|\n)((?:[-*] .+(?:\n|$))+)/g, (_, pre, block) => {
            const items = block.trim().split('\n')
                .map(item => `<li>${item.replace(/^[-*] /, '')}</li>`).join('');
            return `${pre}<ul>${items}</ul>`;
        });

        processed = processed.replace(/(^|\n)((?:\d+\. .+(?:\n|$))+)/g, (_, pre, block) => {
            const items = block.trim().split('\n')
                .map(item => `<li>${item.replace(/^\d+\. /, '')}</li>`).join('');
            return `${pre}<ol>${items}</ol>`;
        });

        processed = processed
            .split(/\n\n+/)
            .map(block => {
                block = block.trim();
                if (!block) return '';
                if (/^<(h[1-6]|ul|ol|pre|blockquote)/.test(block) || /^__(CB|ML)_/.test(block)) return block;
                return `<p>${block.replace(/\n/g, '<br>')}</p>`;
            })
            .join('');

        codeBlocks.forEach((html, i) => {
            processed = processed.replace(`__CB_${i}__`, html);
        });
        inlineCodes.forEach((html, i) => {
            processed = processed.replace(`__IC_${i}__`, html);
        });
        markdownLinks.forEach((html, i) => {
            processed = processed.replace(`__ML_${i}__`, html);
        });

        return processed;
    }
}

export { ChatInterface };

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.mod-ask-confused-ai').forEach(container => {
        const apiUrl = container.dataset.apiUrl || 'http://localhost:8000/orchestrator';
        const apiSubKey = container.dataset.apiKey || '';
        const userJwt = container.dataset.userJwt || '';
        new ChatInterface({ apiUrl, apiSubKey, userJwt, container });
    });
});

